import React, { useState } from 'react';
import { ContentEditorProps } from './types';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { X, Plus, Image, ChevronDown, ChevronUp, Trash2, Loader2, Check } from 'lucide-react';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import BrandResources from '@/components/brand/BrandResources';
import { cn } from '@/lib/utils';
import { Database } from '../../../../database.types';
import { useUnsavedChangesPrompt } from '@/hooks/useUnsavedChangesPrompt';
import { ToastSave } from '@/components/ui/toast-save';
import {
  type PlatformPostType,
  getPlatformPostType,
  getPostTypesForPlatform,
  getDefaultSinglePostType,
  isSeriesFromPostType,
  POST_TYPE_LABELS
} from '@/types/content.types';

// Define the ResourceCache type from database types
type ResourceCache = Database['public']['Tables']['resource_cache']['Row'];

// Define interfaces for post structure
interface PostData {
  text: string;
  hashtags: string[];
  image_url?: string;
  calls_to_action: string[];
}

// Post type option for UI display
interface PostTypeOption {
  value: PlatformPostType;
  label: string;
  description: string;
  whenToUse: string; // Guidance on when to use this post type
}

/**
 * Get platform-specific post type options for the select dropdown
 * Uses centralized POST_TYPE_LABELS mapping for consistency
 *
 * @param platform - The social media platform
 * @returns Array of post type options with labels, descriptions, and usage guidance
 */
function getPlatformPostTypeOptions(platform: string): PostTypeOption[] {
  const postTypes = getPostTypesForPlatform(platform);

  // Use centralized POST_TYPE_LABELS for consistency across frontend and backend
  return postTypes.map(type => ({
    value: type,
    label: POST_TYPE_LABELS[type]?.label || type,
    description: POST_TYPE_LABELS[type]?.description || '',
    whenToUse: POST_TYPE_LABELS[type]?.whenToUse || ''
  }));
}

/**
 * Editor for social post content type with support for images and videos
 * Supports platform-specific post types with backward compatibility for is_series
 */
export const SocialPostEditor: React.FC<ContentEditorProps> = ({ 
  content, 
  onSave, 
  onCancel,
  onUnsavedChanges
}) => {
  
  const brandId = content.brand_id;

  // Extract social post specific data with backward compatibility
  const contentJson = content.content_json || {};
  const { platform = 'twitter', post_type = 'text' } = contentJson;

  // Get platform_post_type with backward compatibility fallback
  const platformPostType = getPlatformPostType(contentJson);

  // Initialize posts array - either from existing content or with a blank post
  const initialPosts: PostData[] = contentJson.posts && contentJson.posts.length > 0
    ? contentJson.posts.map((post: any) => ({
        text: post.text || '',
        hashtags: post.hashtags || [],
        image_url: post.image_url || '',
        calls_to_action: post.calls_to_action || []
      }))
    : contentJson.text
      ? [{
          text: contentJson.text || '',
          hashtags: contentJson.hashtags || [],
          image_url: '',
          calls_to_action: []
        }]
      : [{
          text: '',
          hashtags: [],
          image_url: '',
          calls_to_action: []
        }];

  // Set up state for form - using platform_post_type instead of is_series
  const [formState, setFormState] = useState({
    platform: platform,
    platform_post_type: platformPostType,
    post_type: post_type,
    posts: initialPosts
  });
  const [initialFormState, setInitialFormState] = useState(formState);
  const [unsavedChanges, setUnsavedChanges] = useState(false);
  const [saveState, setSaveState] = useState<'initial' | 'loading' | 'success' | 'error'>('initial');
  const [saveError, setSaveError] = useState<string>('');
  useUnsavedChangesPrompt(unsavedChanges);
  
  // Active post index for editing (default to first post)
  const [activePostIndex, setActivePostIndex] = useState(0);

  // State for media picker dialog
  const [imagePickerOpen, setImagePickerOpen] = useState(false);
  const [imagePickerPostIndex, setImagePickerPostIndex] = useState(0);

  // Compute whether this is a series/multi-post type based on platform_post_type
  const isSeries = isSeriesFromPostType(formState.platform_post_type);
  
  // Function to check if a file is a video based on extension
  const isVideoFile = (url: string): boolean => {
    if (!url) return false;
    const videoExtensions = ['.mp4', '.mov', '.avi', '.webm', '.mkv', '.flv', '.wmv', '.m4v', '.ogv'];
    const extension = url.toLowerCase().split('.').pop();
    return videoExtensions.includes(`.${extension}`);
  };
  
  // Handle platform change - reset post type to platform default
  const handlePlatformChange = (value: string) => {
    const defaultPostType = getDefaultSinglePostType(value);
    setFormState(prev => ({
      ...prev,
      platform: value,
      platform_post_type: defaultPostType
    }));
  };

  // Handle post type change
  const handlePostTypeChange = (value: string) => {
    setFormState(prev => ({
      ...prev,
      platform_post_type: value as PlatformPostType
    }));
  };
  
  // Handle post text change
  const handlePostTextChange = (index: number, value: string) => {
    setFormState(prev => {
      const newPosts = [...prev.posts];
      newPosts[index] = { ...newPosts[index], text: value };
      return { ...prev, posts: newPosts };
    });
  };

  // Add a new post to the thread
  const handleAddPost = () => {
    setFormState(prev => ({
      ...prev,
      posts: [
        ...prev.posts,
        { text: '', hashtags: [], image_url: '', calls_to_action: [] }
      ]
    }));
    // Auto-activate the new post
    setActivePostIndex(formState.posts.length);
  };

  // Remove a post from the thread
  const handleRemovePost = (index: number) => {
    setFormState(prev => {
      const newPosts = [...prev.posts];
      newPosts.splice(index, 1);
      
      // Ensure we always have at least one post
      if (newPosts.length === 0) {
        newPosts.push({ text: '', hashtags: [], image_url: '', calls_to_action: [] });
      }
      
      return { ...prev, posts: newPosts };
    });
    
    // Adjust active post index if needed
    if (activePostIndex >= index && activePostIndex > 0) {
      setActivePostIndex(activePostIndex - 1);
    }
  };

  // Move post up or down in the thread
  const handleMovePost = (index: number, direction: 'up' | 'down') => {
    if (
      (direction === 'up' && index === 0) || 
      (direction === 'down' && index === formState.posts.length - 1)
    ) {
      return; // Can't move beyond boundaries
    }

    const newIndex = direction === 'up' ? index - 1 : index + 1;
    
    setFormState(prev => {
      const newPosts = [...prev.posts];
      const post = newPosts[index];
      newPosts.splice(index, 1);
      newPosts.splice(newIndex, 0, post);
      return { ...prev, posts: newPosts };
    });
    
    // Update active post index to follow the moved post
    setActivePostIndex(newIndex);
  };
  
  // Handle hashtag management for a specific post
  const [newHashtag, setNewHashtag] = useState('');
  
  const handleAddHashtag = (postIndex: number) => {
    if (!newHashtag.trim()) return;
    
    const tag = newHashtag.startsWith('#') ? newHashtag : `#${newHashtag}`;
    
    setFormState(prev => {
      const newPosts = [...prev.posts];
      if (!newPosts[postIndex].hashtags.includes(tag)) {
        newPosts[postIndex] = {
          ...newPosts[postIndex],
          hashtags: [...newPosts[postIndex].hashtags, tag]
        };
      }
      return { ...prev, posts: newPosts };
    });
    
    setNewHashtag('');
  };
  
  const handleRemoveHashtag = (postIndex: number, tag: string) => {
    setFormState(prev => {
      const newPosts = [...prev.posts];
      newPosts[postIndex] = {
        ...newPosts[postIndex],
        hashtags: newPosts[postIndex].hashtags.filter(t => t !== tag)
      };
      return { ...prev, posts: newPosts };
    });
  };
  
  // Handle call to action management for a specific post
  const [newCta, setNewCta] = useState('');
  
  const handleAddCta = (postIndex: number) => {
    if (!newCta.trim()) return;
    
    setFormState(prev => {
      const newPosts = [...prev.posts];
      if (!newPosts[postIndex].calls_to_action.includes(newCta)) {
        newPosts[postIndex] = {
          ...newPosts[postIndex],
          calls_to_action: [...newPosts[postIndex].calls_to_action, newCta]
        };
      }
      return { ...prev, posts: newPosts };
    });
    
    setNewCta('');
  };
  
  const handleRemoveCta = (postIndex: number, cta: string) => {
    setFormState(prev => {
      const newPosts = [...prev.posts];
      newPosts[postIndex] = {
        ...newPosts[postIndex],
        calls_to_action: newPosts[postIndex].calls_to_action.filter(c => c !== cta)
      };
      return { ...prev, posts: newPosts };
    });
  };
  
  // Handle image selection
  const handleOpenImagePicker = (postIndex: number) => {
    setImagePickerPostIndex(postIndex);
    setImagePickerOpen(true);
  };
  
  const handleSelectImage = (resource: ResourceCache) => {
    if (resource && resource.url_to_inject) {
      setFormState(prev => {
        const newPosts = [...prev.posts];
        newPosts[imagePickerPostIndex] = {
          ...newPosts[imagePickerPostIndex],
          image_url: resource.url_to_inject || ''
        };
        return { ...prev, posts: newPosts };
      });
    }
    setImagePickerOpen(false);
  };
  
  const handleRemoveImage = (postIndex: number) => {
    setFormState(prev => {
      const newPosts = [...prev.posts];
      newPosts[postIndex] = {
        ...newPosts[postIndex],
        image_url: ''
      };
      return { ...prev, posts: newPosts };
    });
  };
  
  // Get platform character limit (if applicable)
  const getCharacterLimit = (): number | undefined => {
    switch (formState.platform) {
      case 'twitter':
      case 'x':
        return 280;
      default:
        return undefined;
    }
  };
  
  // Track unsaved changes
  React.useEffect(() => {
    const hasChanges = JSON.stringify(formState) !== JSON.stringify(initialFormState);
    setUnsavedChanges(hasChanges);
    // Notify parent about unsaved changes
    if (onUnsavedChanges) {
      onUnsavedChanges(hasChanges);
    }
  }, [formState, initialFormState, onUnsavedChanges]);
  
  // Handle form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaveState('loading');
    setSaveError('');
    try {
      // Create updated content object with new platform_post_type field
      const updatedContent: Partial<typeof content> = {
        content_json: {
          posts: formState.posts,
          platform: formState.platform,
          platform_post_type: formState.platform_post_type,
          post_type: formState.post_type
        },
        name: `${formState.posts[0].text.substring(0, 30)}${formState.posts[0].text.length > 30 ? '...' : ''}`
      };
      await onSave(updatedContent, true);
      setInitialFormState(formState);
      setSaveState('success');
      setTimeout(() => setSaveState('initial'), 3000);
    } catch (error) {
      setSaveState('error');
      const errorMessage = error instanceof Error ? error.message : 'Failed to save changes. Please try again.';
      setSaveError(errorMessage);
      console.error('Error saving social post:', error);
      // Reset error state after 5 seconds
      setTimeout(() => {
        setSaveState('initial');
        setSaveError('');
      }, 5000);
    }
  };
  
  // For ToastSave (no event param)
  const handleSave = () => {
    handleSubmit({ preventDefault: () => {} } as React.FormEvent);
  };
  const handleReset = () => {
    setFormState(initialFormState);
    setSaveState('initial');
  };
  
  return (
    <div className="w-full h-full overflow-auto bg-white dark:bg-gray-900 p-6">
      {/* ToastSave bar for unsaved changes */}
      {unsavedChanges && (
        <div className="fixed bottom-4 left-1/2 transform -translate-x-1/2 z-50">
          <ToastSave
            state={saveState === 'error' ? 'initial' : saveState}
            onSave={handleSave}
            onReset={handleReset}
            loadingText="Saving changes..."
            successText="All changes saved"
            initialText="You have unsaved changes"
            resetText="Cancel"
            saveText="Save"
          />
        </div>
      )}
      <form onSubmit={handleSubmit}>
        <Card className="w-full max-w-4xl mx-auto">
          <CardHeader>
            <CardTitle>Edit Social Post</CardTitle>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
              <div className="flex flex-col gap-2">
                <Label htmlFor="platform">Platform</Label>
                <Select
                  value={formState.platform}
                  onValueChange={handlePlatformChange}
                >
                  <SelectTrigger id="platform">
                    <SelectValue placeholder="Select platform" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="twitter">Twitter/X</SelectItem>
                    <SelectItem value="facebook">Facebook</SelectItem>
                    <SelectItem value="instagram">Instagram</SelectItem>
                    <SelectItem value="linkedin">LinkedIn</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="flex flex-col gap-2">
                <Label htmlFor="post-type-select">Post Type</Label>
                <Select
                  value={formState.platform_post_type}
                  onValueChange={handlePostTypeChange}
                >
                  <SelectTrigger id="post-type-select">
                    <SelectValue placeholder="Select post type" />
                  </SelectTrigger>
                  <SelectContent>
                    {getPlatformPostTypeOptions(formState.platform).map(option => (
                      <SelectItem key={option.value} value={option.value}>
                        <div className="flex flex-col py-1">
                          <div className="font-medium">{option.label}</div>
                          <div className="text-xs text-muted-foreground">{option.description}</div>
                          <div className="text-xs text-blue-600 dark:text-blue-400 mt-1 italic">{option.whenToUse}</div>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {/* Show guidance for selected post type */}
                {formState.platform_post_type && POST_TYPE_LABELS[formState.platform_post_type] && (
                  <p className="text-xs text-muted-foreground italic">
                    💡 {POST_TYPE_LABELS[formState.platform_post_type].whenToUse}
                  </p>
                )}
              </div>
            </div>
          </CardHeader>
          
          <CardContent className="space-y-6">
            {/* Post tabs for multiple posts */}
            {formState.posts.length > 1 && (
              <Tabs 
                value={activePostIndex.toString()} 
                onValueChange={val => setActivePostIndex(parseInt(val))}
                className="w-full"
              >
                <div className="flex items-center justify-between mb-2">
                  <div className="text-sm text-muted-foreground">
                    {isSeries ? 'Thread' : 'Posts'} · {formState.posts.length} {formState.posts.length === 1 ? 'post' : 'posts'}
                  </div>
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={handleAddPost}
                  >
                    <Plus className="h-4 w-4 mr-1" />
                    Add Post
                  </Button>
                </div>
                <TabsList className="w-full overflow-x-auto whitespace-nowrap">
                  {formState.posts.map((_, index) => (
                    <TabsTrigger
                      key={index}
                      value={index.toString()}
                      className={cn(
                        "relative min-w-[90px]",
                        isSeries && "flex items-center justify-center"
                      )}
                    >
                      {isSeries ? (
                        <span className="flex items-center">
                          Post {index + 1} / {formState.posts.length}
                        </span>
                      ) : (
                        <span>Post {index + 1}</span>
                      )}
                    </TabsTrigger>
                  ))}
                </TabsList>
              </Tabs>
            )}
            
            {/* Active post editor */}
            <div className="space-y-6 pt-4 border rounded-lg p-6">
              {/* Post actions (only show if multiple posts) */}
              {formState.posts.length > 1 && (
                <div className="flex justify-between items-center">
                  <div className="text-lg font-medium">
                    {isSeries ? `Post ${activePostIndex + 1} / ${formState.posts.length}` : `Post ${activePostIndex + 1}`}
                  </div>
                  <div className="flex space-x-2">
                    <Button 
                      type="button" 
                      variant="outline" 
                      size="icon"
                      className="h-8 w-8"
                      onClick={() => handleMovePost(activePostIndex, 'up')}
                      disabled={activePostIndex === 0}
                    >
                      <ChevronUp className="h-4 w-4" />
                    </Button>
                    <Button 
                      type="button" 
                      variant="outline" 
                      size="icon"
                      className="h-8 w-8"
                      onClick={() => handleMovePost(activePostIndex, 'down')}
                      disabled={activePostIndex === formState.posts.length - 1}
                    >
                      <ChevronDown className="h-4 w-4" />
                    </Button>
                    <Button 
                      type="button" 
                      variant="outline" 
                      size="icon"
                      className="h-8 w-8 text-red-500 hover:text-red-700"
                      onClick={() => handleRemovePost(activePostIndex)}
                      disabled={formState.posts.length <= 1}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              )}
              
              {/* Post text */}
              <div className="space-y-2">
                <Label htmlFor={`post-text-${activePostIndex}`}>Post Content</Label>
                <Textarea
                  id={`post-text-${activePostIndex}`}
                  value={formState.posts[activePostIndex]?.text || ''}
                  onChange={(e) => handlePostTextChange(activePostIndex, e.target.value)}
                  placeholder="What's on your mind?"
                  className="min-h-[120px]"
                  required
                />
                
                {getCharacterLimit() !== undefined && (
                  <div className={`text-xs text-right ${
                    (formState.posts[activePostIndex]?.text?.length || 0) > getCharacterLimit()! 
                      ? 'text-red-500 font-medium'
                      : 'text-muted-foreground'
                  }`}>
                    {formState.posts[activePostIndex]?.text?.length || 0}/{getCharacterLimit()} characters
                  </div>
                )}
              </div>
              
              {/* Post media */}
              <div className="space-y-2">
                <Label>Post Media</Label>
                {formState.posts[activePostIndex]?.image_url ? (
                  <div className="relative">
                    {isVideoFile(formState.posts[activePostIndex].image_url) ? (
                      <video 
                        src={formState.posts[activePostIndex].image_url}
                        className="w-full max-h-[300px] object-contain rounded-md border"
                        controls
                        preload="metadata"
                      >
                        Your browser does not support the video tag.
                      </video>
                    ) : (
                      <img 
                        src={formState.posts[activePostIndex].image_url} 
                        alt="Post media"
                        className="w-full max-h-[300px] object-contain rounded-md border"
                      />
                    )}
                    <div className="absolute top-2 right-2 flex space-x-2">
                      <Button
                        type="button"
                        variant="destructive"
                        size="icon"
                        className="h-8 w-8 bg-white text-red-500 hover:bg-red-50 hover:text-red-600 border border-red-200"
                        onClick={() => handleRemoveImage(activePostIndex)}
                      >
                        <X className="h-4 w-4" />
                      </Button>
                      <Button
                        type="button"
                        variant="outline"
                        size="icon"
                        className="h-8 w-8 bg-white hover:bg-gray-50"
                        onClick={() => handleOpenImagePicker(activePostIndex)}
                      >
                        <Image className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div 
                    className="w-full h-[200px] border-2 border-dashed rounded-md flex flex-col items-center justify-center cursor-pointer hover:bg-muted/50 transition-colors"
                    onClick={() => handleOpenImagePicker(activePostIndex)}
                  >
                    <Image className="h-10 w-10 text-muted-foreground mb-2" />
                    <p className="text-sm text-muted-foreground">Click to add media (image or video)</p>
                  </div>
                )}
              </div>
              
              {/* Hashtags */}
              <div className="space-y-2">
                <Label htmlFor={`hashtags-${activePostIndex}`}>Hashtags</Label>
                <div className="flex gap-2">
                  <Input
                    id={`hashtags-${activePostIndex}`}
                    value={newHashtag}
                    onChange={(e) => setNewHashtag(e.target.value)}
                    placeholder="Add a hashtag"
                    className="flex-1"
                  />
                  <Button type="button" onClick={() => handleAddHashtag(activePostIndex)}>
                    Add
                  </Button>
                </div>
                
                {formState.posts[activePostIndex]?.hashtags.length > 0 && (
                  <div className="flex flex-wrap gap-2 mt-2">
                    {formState.posts[activePostIndex].hashtags.map((tag, index) => (
                      <div 
                        key={index}
                        className="bg-primary-100 text-primary-800 dark:bg-primary-900 dark:text-primary-300 
                                  px-3 py-1 rounded-full flex items-center gap-2"
                      >
                        <span>{tag}</span>
                        <button 
                          type="button"
                          onClick={() => handleRemoveHashtag(activePostIndex, tag)}
                          className="text-primary-800 dark:text-primary-300 hover:text-primary-900 dark:hover:text-primary-200"
                        >
                          <X className="h-3 w-3" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              
              {/* Call to action */}
              <div className="space-y-2">
                <Label htmlFor={`cta-${activePostIndex}`}>Call to Action</Label>
                <div className="flex gap-2">
                  <Input
                    id={`cta-${activePostIndex}`}
                    value={newCta}
                    onChange={(e) => setNewCta(e.target.value)}
                    placeholder="Add a call to action (e.g., 'Click link in bio', 'Follow for more')"
                    className="flex-1"
                  />
                  <Button type="button" onClick={() => handleAddCta(activePostIndex)}>
                    Add
                  </Button>
                </div>
                
                {formState.posts[activePostIndex]?.calls_to_action.length > 0 && (
                  <div className="flex flex-wrap gap-2 mt-2">
                    {formState.posts[activePostIndex].calls_to_action.map((cta, index) => (
                      <div 
                        key={index}
                        className="bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300 
                                  px-3 py-1 rounded-full flex items-center gap-2"
                      >
                        <span>{cta}</span>
                        <button 
                          type="button"
                          onClick={() => handleRemoveCta(activePostIndex, cta)}
                          className="text-blue-800 dark:text-blue-300 hover:text-blue-900 dark:hover:text-blue-200"
                        >
                          <X className="h-3 w-3" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
            
            {/* Add post button (at the bottom) */}
            <div className="flex justify-center">
              <Button 
                type="button" 
                variant="outline" 
                onClick={handleAddPost}
              >
                <Plus className="h-4 w-4 mr-2" />
                Add Post
              </Button>
            </div>
          </CardContent>
          
          <CardFooter className="flex flex-col gap-2">
            {/* Error message display */}
            {saveError && (
              <div className="w-full p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-md">
                <p className="text-sm text-red-600 dark:text-red-400">{saveError}</p>
              </div>
            )}

            {/* Action buttons */}
            <div className="flex justify-between space-x-2 w-full">
              <Button type="button" variant="outline" onClick={onCancel} disabled={saveState === 'loading'}>
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={saveState === 'loading'}
                className={cn(
                  saveState === 'success' && 'bg-green-600 hover:bg-green-700',
                  saveState === 'error' && 'bg-red-600 hover:bg-red-700'
                )}
              >
                {saveState === 'loading' && (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                )}
                {saveState === 'success' && (
                  <Check className="mr-2 h-4 w-4" />
                )}
                {saveState === 'loading' && 'Saving...'}
                {saveState === 'success' && 'Saved!'}
                {saveState === 'error' && 'Save Failed'}
                {saveState === 'initial' && 'Save Changes'}
              </Button>
            </div>
          </CardFooter>
        </Card>
      </form>
      
      {/* Media Picker Dialog */}
      <Dialog open={imagePickerOpen} onOpenChange={setImagePickerOpen}>
        <DialogContent className="max-w-5xl h-[80vh] flex flex-col">
          <DialogTitle>Select Media</DialogTitle>
          <div className="flex-grow overflow-hidden">
            <BrandResources 
              mode="picker"
              onResourceSelect={handleSelectImage}
              onCancel={() => setImagePickerOpen(false)}
              allowedTypes={['image', 'video']}
              brandId={brandId}
            />
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SocialPostEditor; 